import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { hasAnyRole } from '../../config/areas';

interface RequireRoleProps {
  anyOf: readonly string[];
  roles: readonly string[];
  loading: boolean;
  children: ReactNode;
}

/**
 * Client-side gate, defence in depth only — SWA's route rules are the real
 * boundary and have already run before this component renders. Takes roles as
 * props rather than fetching, so an area mounts exactly one /.auth/me request.
 */
export function RequireRole({ anyOf, roles, loading, children }: RequireRoleProps) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <p className="text-sm text-muted-foreground" aria-live="polite">
        {t('common.loading')}
      </p>
    );
  }

  if (!hasAnyRole(roles, anyOf)) {
    return (
      <section className="rounded-lg border border-border bg-surface p-6">
        <h1 className="text-xl font-heading font-semibold text-foreground">
          {t('auth.forbidden.title')}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">{t('auth.forbidden.description')}</p>
      </section>
    );
  }

  return <>{children}</>;
}
